import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import DateSelector from "../../../components/shared/fields/DateSelector";
import GridCard from "../../../components/shared/gridCard/GridCard";
import InfoCard from "../../../components/shared/infocard/InfoCard";
import Button from "../../../components/shared/buttons/button";
import useTranslator from "../../../hooks/useTranslator";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import NoDataFound from "../../../components/shared/noDataFound/NoDataFound";
import CheekStripModal from "../packing/CheekStripModal";
import SeparationModal from "../bagging/SeparationModal";
import KgGreen from "../../../images/kg_green.svg";
import {
  useGetCheeksStripsDataQuery,
  useGetSortingSummaryQuery,
  useGetSeparationRecordQuery,
  useCreateSeparationRecordMutation,
  useUpdateSeparationRecordMutation,
} from "../packing/packingApi";
import { formatDateAndTime, getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";

const PackingSorting = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [addData, setAddData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("");
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(date));
  const [stripLogButton, setStripLogButton] = useState("Add Data");
  const [cheekLogButton, setCheekLogButton] = useState("Add Data");
  
  // Separation modal states
  const [showDaySeparationModal, setShowDaySeparationModal] = useState(false);
  const [showNightSeparationModal, setShowNightSeparationModal] = useState(false);
  const [daySeparationMode, setDaySeparationMode] = useState("add");
  const [nightSeparationMode, setNightSeparationMode] = useState("add");
  const [daySeparationData, setDaySeparationData] = useState(null);
  const [nightSeparationData, setNightSeparationData] = useState(null);

  const { data: cheekData, isLoading: cheeksDataLoading } = useGetCheeksStripsDataQuery(
    {
      type: "cheeks",
      date,
      lot_number:lotNumber
    },
    { refetchOnMountOrArgChange: true }
  );

  const { data: stripsData } = useGetCheeksStripsDataQuery(
    {
      type: "strips",
      date,
      lot_number:lotNumber
    },
    { refetchOnMountOrArgChange: true }
  );

  const { data: sortingSummaryData } = useGetSortingSummaryQuery({ date:date ,lot_number:lotNumber });
  
  const { data: daySeparationRecordData } = useGetSeparationRecordQuery({ date, shift: "day", separation_area: "Packing_and_Sorting" ,lot_number:lotNumber });
  const { data: nightSeparationRecordData } = useGetSeparationRecordQuery({ date, shift: "night", separation_area: "Packing_and_Sorting",lot_number:lotNumber });
  const [createSeparationRecord] = useCreateSeparationRecordMutation();
  const [updateSeparationRecord] = useUpdateSeparationRecordMutation();

  useEffect(() => {
    setLotNumber(makePriorLotNumber(date));
  }, [date]);

  useEffect(() => {
    // Update day separation data
    if (daySeparationRecordData?.data) {
      setDaySeparationData(daySeparationRecordData.data);
    } else {
      setDaySeparationData(null);
    }
  }, [daySeparationRecordData]);

  useEffect(() => {
    // Update night separation data
    if (nightSeparationRecordData?.data) {
      setNightSeparationData(nightSeparationRecordData.data);
    } else {
      setNightSeparationData(null);
    }
  }, [nightSeparationRecordData]);

  useEffect(() => {
    if (cheekData !== null && cheekData?.data !== null) {
      setCheekLogButton(_t("edit_data", "Edit Data"));
    } else {
      setCheekLogButton(_t("add_data", "Add Data"));
    }

    if (stripsData !== null && stripsData?.data !== null) {
      setStripLogButton(_t("edit_data", "Edit Data"));
    } else {
      setStripLogButton(_t("add_data", "Add Data"));
    }
  }, [cheekData, stripsData, _t]);

  const handleCheekAdd = () => {
    setAddData(true);
    setModalMode("cheeks");
    if (cheekData && cheekData.data != null) {
      setModalMode("editCheeks");
      setSelectedRecord(cheekData);
    } else {
      setSelectedRecord(null);
    }
    setShowModal(true);
  };

  const handleStripAdd = () => {
    setAddData(true);
    setModalMode("strips");
    setSelectedRecord(null);

    if (stripsData && stripsData.data != null) {
      setModalMode("editStrips");
      setSelectedRecord(stripsData);
    } else {
      setSelectedRecord(null);
    }
    setShowModal(true);
  };

  const handleDaySeparationAdd = () => {
    setShowDaySeparationModal(true);
    setDaySeparationMode(daySeparationData ? "edit" : "add");
  };

  const handleNightSeparationAdd = () => {
    setShowNightSeparationModal(true);
    setNightSeparationMode(nightSeparationData ? "edit" : "add");
  };

  const handleDaySeparationSave = async (data) => {
    try {
      if (daySeparationData && daySeparationData.id) {
        await updateSeparationRecord({ 
          id: daySeparationData.id, 
          requestBody: data 
        }).unwrap();
      } else {
        await createSeparationRecord(data).unwrap();
      }
    } catch (error) {
      console.error("Error saving day separation data:", error);
    }
  };

  const handleNightSeparationSave = async (data) => {
    try {
      if (nightSeparationData && nightSeparationData.id) {
        await updateSeparationRecord({ 
          id: nightSeparationData.id, 
          requestBody: data 
        }).unwrap();
      } else {
        await createSeparationRecord(data).unwrap();
      }
    } catch (error) {
      console.error("Error saving night separation data:", error);
    }
  };

  const cheeksLogData = Array.isArray(cheekData?.data)
    ? cheekData.data.map((item) => [
        { label: _t("prime", "Prime"), value: item.prime },
        { label: _t("seconds", "Seconds"), value: item.seconds },
        { label: _t("humidity", "Humidity"), value: item.humidity },
        { label: _t("green", "Green"), value: item.green },
        { label: _t("bone_seed", "Bone/Seed"), value: item.bone_seed },
        { label: _t("toasted", "Toasted"), value: item.toasted },
        {
          label: _t("strange_material", "Foreign Material"),
          value: item.strange_material,
        },
        { label: _t("spot", "Spot"), value: item.spot },
        { label: _t("pieces", "Pieces"), value: item.pieces },
        { label: _t("tapita", "Tapita"), value: item.tapita },
        { label: _t("pasting", "Pasting"), value: item.pasting || 0  },
      ])
    : cheekData?.data
    ? [
        [
          { label: _t("prime", "Prime"), value: cheekData.data.prime },
          { label: _t("seconds", "Seconds"), value: cheekData.data.seconds },
          { label: _t("humidity", "Humidity"), value: cheekData.data.humidity },
          { label: _t("green", "Green"), value: cheekData.data.green  },
          {
            label: _t("bone_seed", "Bone/Seed"),
            value: cheekData.data.bone_seed,
          },
          { label: _t("toasted", "Toasted"), value: cheekData.data.toasted },
          {
            label: _t("strange_material", "Foreign Material"),
            value: cheekData.data.strange_material,
          },
          { label: _t("spot", "Spot"), value: cheekData.data.spot },
        { label: _t("pieces", "Pieces"), value: cheekData.data.pieces },
        { label: _t("tapita", "Tapita"), value: cheekData.data.tapita },
        { label: _t("pasting", "Pasting"), value: cheekData?.data.pasting || 0  }
        ],
      ]
    : [];

  const stripsLogData = Array.isArray(stripsData?.data?.data)
    ? stripsData.map((item) => [
        { label: _t("prime", "Prime"), value: item.prime },
        { label: _t("seconds", "Seconds"), value: item.seconds },
        { label: _t("humidity", "Humidity"), value: item.humidity },
        { label: _t("green", "Green"), value: item.green },
        { label: _t("bone_seed", "Bone/Seed"), value: item.bone_seed },
        { label: _t("toasted", "Toasted"), value: item.toasted },
        {
          label: _t("strange_material", "Foreign Material"),
          value: item.strange_material,
        },
        { label: _t("spot", "Spot"), value: item.spot },
        { label: _t("pieces", "Pieces"), value: item.pieces },
        { label: _t("pasting", "Pasting"), value: item.pasting || 0  },

      ])
    : stripsData?.data
    ? [
        [
          { label: _t("prime", "Prime"), value: stripsData.data.prime },
          { label: _t("seconds", "Seconds"), value: stripsData.data.seconds },
          {
            label: _t("humidity", "Humidity"),
            value: stripsData.data.humidity,
          },
          { label: _t("green", "Green"), value: stripsData.data.green },
          {
            label: _t("bone_seed", "Bone/Seed"),
            value: stripsData.data.bone_seed,
          },
          { label: _t("toasted", "Toasted"), value: stripsData.data.toasted },
          {
            label: _t("strange_material", "Foreign Material"),
            value: stripsData.data.strange_material,
          },
          { label: _t("spot", "Spot"), value: stripsData.data.spot },
          { label: _t("pieces", "Pieces"), value: stripsData.data.pieces || 0 },
        { label: _t("pasting", "Pasting"), value: stripsData?.data?.pasting || 0  },

        ],
      ]
    : [];

  const listItems = [
    {
      label: _t("sorting_total_kg_entered", "Total KG Entered"),
      value: sortingSummaryData?.data?.total_kg_entered || 0,
      icon: KgGreen,
    },
    {
      label: _t("sorting_cheeks_kg_entered", "Cheeks KG Entered"),
      value: sortingSummaryData?.data?.cheeks_kg_entered || 0,
      icon: KgGreen,
    },
    {
      label: _t("sorting_tapita_kg_entered", "Tapita KG Entered"),
      value: sortingSummaryData?.data?.tapita_kg_entered || 0,
      icon: KgGreen,
    },
    {
      label: _t("sorting_strips_kg_entered", "Strips KG Entered"),
      value: sortingSummaryData?.data?.strips_kg_entered || 0,
      icon: KgGreen,
    },
    {
      label: _t("sorting_total_prime_kg", "Total Prime KG"),
      value: sortingSummaryData?.data?.total_prime_kg || 0,
      icon: KgGreen,
    },
    {
      label: _t("sorting_total_prime_cheeks_kg", "Total Prime Cheeks KG"),
      value: sortingSummaryData?.data?.total_prime_cheeks_kg || 0,
      icon: KgGreen,
    },
    {
      label: _t("sorting_total_prime_strips_kg", "Total Prime Strips KG"),
      value: sortingSummaryData?.data?.total_prime_strips_kg || 0,
      icon: KgGreen,
    },
  ];

  const total_cheeks_kg = sortingSummaryData?.data?.cheeks_kg_entered ?? 0;
  const total_strips_kg = sortingSummaryData?.data?.strips_kg_entered ?? 0;

  const daySeparationLogData = daySeparationData
    ? [
        [
          { label: _t("strips_kg", "Strips KG"), value: daySeparationData.strips_kg || 0 },
          { label: _t("cheeks_kg", "Cheeks KG"), value: daySeparationData.cheeks_kg || 0 },
          { label: _t("number_of_people_separating", "Number of people Separating"), value: daySeparationData.total_people || 0 },
        ],
      ]
    : [];

  const nightSeparationLogData = nightSeparationData
    ? [
        [
          { label: _t("strips_kg", "Strips KG"), value: nightSeparationData.strips_kg || 0 },
          { label: _t("cheeks_kg", "Cheeks KG"), value: nightSeparationData.cheeks_kg || 0 },
          { label: _t("number_of_people_separating", "Number of people Separating"), value: nightSeparationData.total_people || 0 },
        ],
      ]
    : [];

  return (
    <div>
      {addData && (
        <CheekStripModal
          show={showModal}
          setShow={setAddData}
          mode={modalMode}
          record={selectedRecord}
          lot_number_id={lotNumber}
          total_cheeks_kg={total_cheeks_kg}
          total_strips_kg={total_strips_kg}
        />
      )}

      <SeparationModal
        show={showDaySeparationModal}
        setShow={setShowDaySeparationModal}
        record={daySeparationData}
        mode={daySeparationMode}
        onSave={handleDaySeparationSave}
        date={date}
        lotNumber={lotNumber}
        shift="day"
        separationArea="Packing_and_Sorting"
      />

      <SeparationModal
        show={showNightSeparationModal}
        setShow={setShowNightSeparationModal}
        record={nightSeparationData}
        mode={nightSeparationMode}
        onSave={handleNightSeparationSave}
        date={date}
        lotNumber={lotNumber}
        shift="night"
        separationArea="Packing_and_Sorting"
      />

      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">
            {_t("packing_sorting_overview", "Packing Sorting Overview")}
          </h6>
          <div className="d-flex gap-2 align-items-center">
            <h6 className="mb-0">{_t("batch_code", "Batch Code")}</h6>
            <Button label={`#${lotNumber ?? 0}`} size="small" className="borderRedBtn" />
            <DateSelector setDate={setDate} />
          </div>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard
              key={index}
              title={list.label}
              value={list.value}
              icon={list.icon}
              day={currentDate === date ? _t("common_today", "Today") : formatDateAndTime(date, PROJECT_DATE_FORMAT)}
            />
          ))}
        </ul>
      </div>

      <div className="my-3">
        <h6 className="mb-3">
          {_t("cheeks_sorting_log", "Cheeks/Strips Sorting Log")}
        </h6>
        <div className="gap-2 overviewcard">
          <Row>
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">
                      {_t("cheeks_log", "Cheeks Log")}
                    </h3>
                    <span className="shift-badge packing-lot-badge">{`${_t('lot_no','Lot No.')} #${lotNumber}`}</span>
                  </div>
                  {!cheeksDataLoading && (date === currentDate || !cheekData?.data) && (
                    <Button
                      size="xs"
                      label={cheekLogButton}
                      icon={PlusSvg}
                      onClick={handleCheekAdd}
                    />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {cheeksLogData.length > 0 ? (
                      cheeksLogData.map((data, index) => (
                        <Row key={index}>
                          {data.map((item, itemIndex) => (
                            <Col lg={6} key={`${index}-${itemIndex}`}>
                              <InfoCard label={item.label} value={item.value} />
                            </Col>
                          ))}
                        </Row>
                      ))
                    ) : (
                      <NoDataFound />
                    )}
                  </Row>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">
                      {_t("strips_log", "Strips Log")}
                    </h3>
                    <span className="shift-badge packing-lot-badge">{`${_t('lot_no','Lot No.')} #${lotNumber}`}</span>
                  </div>
                  {(date === currentDate || !stripsData?.data) && (
                    <Button
                      size="xs"
                      label={stripLogButton}
                      icon={PlusSvg}
                      onClick={handleStripAdd}
                    />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {stripsLogData.length > 0 ? (
                      stripsLogData.map((data, index) => (
                        <Row key={index}>
                          {data.map((item) => (
                            <Col lg={6} key={item.label}>
                              <InfoCard label={item.label} value={item.value} />
                            </Col>
                          ))}
                        </Row>
                      ))
                    ) : (
                      <NoDataFound />
                    )}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>

        {/*  */}
          <Row className="mt-3">
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">
                      {_t("day_separation", "Day Separation")}
                    </h3>
                    <span className="shift-badge packing-lot-badge">{`${_t('lot_no','Lot No.')} #${lotNumber}`}</span>
                  </div>
                  {(date === currentDate || !daySeparationData) && (
                    <Button
                      size="xs"
                      label={daySeparationData ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                      icon={PlusSvg}
                      onClick={handleDaySeparationAdd}
                    />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {daySeparationLogData.length > 0 ? (
                      daySeparationLogData.map((data, index) => (
                        <Row key={index}>
                          {data.map((item, itemIndex) => (
                            <Col lg={6} key={`${index}-${itemIndex}`}>
                              <InfoCard label={item.label} value={item.value} />
                            </Col>
                          ))}
                        </Row>
                      ))
                    ) : (
                      <NoDataFound />
                    )}
                  </Row>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">
                      {_t("night_separation", "Night Separation")}
                    </h3>
                    <span className="shift-badge packing-lot-badge">{`${_t('lot_no','Lot No.')} #${lotNumber}`}</span>
                  </div>
                  {(date === currentDate || !nightSeparationData) && (
                    <Button
                      size="xs"
                      label={nightSeparationData ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                      icon={PlusSvg}
                      onClick={handleNightSeparationAdd}
                    />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {nightSeparationLogData.length > 0 ? (
                      nightSeparationLogData.map((data, index) => (
                        <Row key={index}>
                          {data.map((item, itemIndex) => (
                            <Col lg={6} key={`${index}-${itemIndex}`}>
                              <InfoCard label={item.label} value={item.value} />
                            </Col>
                          ))}
                        </Row>
                      ))
                    ) : (
                      <NoDataFound />
                    )}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default PackingSorting;
